import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { useEffect, useState } from 'react'
import { Menu, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Logo } from '@/components/brand/logo'
import { buttonVariants } from '@/components/ui/button'
import { NAV_LINKS } from '@/constants/site'

const SiteHeader: FC = (): ReactNode => {
    const [open, setOpen] = useState(false)

    useEffect(() => {
        if (!open) return

        const handleKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape') setOpen(false)
        }

        window.addEventListener('keydown', handleKey)

        return () => window.removeEventListener('keydown', handleKey)
    }, [open])

    return (
        <header className='sticky top-0 z-50 border-b border-paper/10 bg-ink/90 text-paper backdrop-blur-md'>
            <div className='mx-auto flex h-16 max-w-7xl items-center justify-between gap-3 px-4 sm:gap-6 sm:px-6 lg:px-8'>
                <Link
                    href='/'
                    onClick={() => setOpen(false)}
                    className='rounded-md focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-volt'
                >
                    <Logo variant='compact' tone='dark' className='h-8 w-auto' />
                </Link>

                <nav aria-label='Main' className='hidden md:block'>
                    <ul className='flex items-center gap-1'>
                        {NAV_LINKS.map(item => (
                            <li key={item.href}>
                                <Link
                                    href={item.href}
                                    className='rounded-full px-4 py-2 text-sm font-medium text-paper/75 transition-colors hover:bg-paper/10 hover:text-paper focus-visible:outline-2 focus-visible:outline-volt'
                                >
                                    {item.label}
                                </Link>
                            </li>
                        ))}
                    </ul>
                </nav>

                <div className='flex items-center gap-2'>
                    <Link
                        href='/#contact'
                        onClick={() => setOpen(false)}
                        className={cn(buttonVariants(), 'h-10 rounded-full px-4 font-semibold focus-visible:ring-volt/60 sm:px-5')}
                    >
                        Get a quote
                    </Link>

                    <button
                        type='button'
                        onClick={() => setOpen(value => !value)}
                        aria-expanded={open}
                        aria-controls='mobile-menu'
                        aria-label={open ? 'Close menu' : 'Open menu'}
                        className='flex size-10 items-center justify-center rounded-full text-paper transition-colors hover:bg-paper/10 focus-visible:outline-2 focus-visible:outline-volt md:hidden'
                    >
                        {open ? <X className='size-5' aria-hidden='true' /> : <Menu className='size-5' aria-hidden='true' />}
                    </button>
                </div>
            </div>

            <nav
                id='mobile-menu'
                aria-label='Mobile'
                hidden={!open}
                className='border-t border-paper/10 bg-ink px-4 pb-6 pt-2 md:hidden'
            >
                <ul className='flex flex-col'>
                    {NAV_LINKS.map(item => (
                        <li key={item.href}>
                            <Link
                                href={item.href}
                                onClick={() => setOpen(false)}
                                className='block rounded-xl px-3 py-3.5 font-heading text-lg font-semibold text-paper transition-colors hover:bg-paper/10 focus-visible:outline-2 focus-visible:outline-volt'
                            >
                                {item.label}
                            </Link>
                        </li>
                    ))}
                </ul>
            </nav>
        </header>
    )
}

export default SiteHeader
